import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { MainWrapper, PageContainer, ThemeWrapper } from '@flixbus/honeycomb-react';
import '@flixbus/honeycomb-react/dist/css/honeycomb-helpers.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import './App.css';
import AppHeader from './components/header/AppHeader';
import Loader from './components/loader/Loader';
import CustomNotification from './components/notification/CustomNotification';
import AuthGuard from './components/auth/AuthGuard';
import ErrorBoundary from './components/errorBoundary/ErrorBoundary';
import PageTitle from './components/pageTitle/PageTitle';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { registerLoadingSetter, registerErrorHandler } from './helpers/ApiCall';
import { ERRORS } from './constants/messages';
import WarningPage from './pages/login/WarningPage';

// One chunk per page; a chunk that fails to load (old tab after a deploy) shows the ErrorBoundary page.
const Search = lazy(() => import('./pages/search/Search'));
const Requests = lazy(() => import('./pages/requests/Requests'));
const RequestDetail = lazy(() => import('./pages/requests/detail/RequestDetail'));

// Every page has the header and needs a login. Signed out, the login page sets its own title.
// The PII warning is skipped on npm start, so local development is not interrupted every week.
const Page = ({ title, children }) => (
    <PageContainer Elem="main" hasFixedHeader>
        <AppHeader />
        <AuthGuard>
            <PageTitle title={title} />
            {process.env.NODE_ENV !== 'development' && <WarningPage />}
            {children}
        </AuthGuard>
    </PageContainer>
);

function AppInner() {
    const { setLoading } = useLoading();

    useEffect(() => {
        registerLoadingSetter(setLoading);
    }, [setLoading]);

    const { showNotification } = useNotification();

    useEffect(() => {
        registerErrorHandler(({ type }) => showNotification(ERRORS[type], 'danger'));
        return () => registerErrorHandler(null);
    }, [showNotification]);

    return (
        <BrowserRouter>
            <ThemeWrapper>
                <MainWrapper full>
                    <Loader />
                    <ErrorBoundary>
                        <Suspense fallback={null}>
                            <Routes>
                                <Route
                                    path="/"
                                    element={
                                        <Page title="Search">
                                            <Search />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/requests"
                                    element={
                                        <Page title="Requests">
                                            <Requests />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/requests/detail"
                                    element={
                                        <Page title="Request detail">
                                            <RequestDetail />
                                        </Page>
                                    }
                                />
                            </Routes>
                        </Suspense>
                    </ErrorBoundary>
                    <CustomNotification />
                </MainWrapper>
            </ThemeWrapper>
        </BrowserRouter>
    );
}

function App() {
    return (
        <LoadingProvider>
            <NotificationProvider>
                <AppInner />
            </NotificationProvider>
        </LoadingProvider>
    );
}

export default App;
